import { MapViewerNode } from '@3ddv/dvm-internal';
import {
  BaseSeatDataTransformer,
  BaseSectionDataTransformer,
  SeatPopoverData,
  SectionPopoverData,
} from '@3ddv/software-division-components';
import { Injectable, inject } from '@angular/core';
import { GeneralAvailability, SeatAvailability } from '@core/models';
import { UtilitiesService } from './utilities.service';

/**
 * GroupSales-specific seat data transformer.
 * Extends the base transformer to provide implementation for SeatAvailability data.
 */
class GroupSalesSeatTransformer extends BaseSeatDataTransformer<SeatAvailability> {
  constructor(private utilitiesService: UtilitiesService) {
    super();
  }

  transform(
    seatAvailability: SeatAvailability,
    context?: { node: MapViewerNode; thumbnail?: string }
  ): SeatPopoverData {
    if (!context) {
      throw new Error(
        'Context with MapViewerNode is required for seat transformation'
      );
    }

    const seatInfo = this.utilitiesService.splittedSeatInfoFromId(
      context.node.original_id
    );
    const primaryPrice = seatAvailability.price?.[0]?.value || 0;

    return {
      id: seatAvailability.id,
      section: seatInfo.section,
      row: seatInfo.seat_row,
      seat: seatInfo.seat,
      price: primaryPrice,
      thumbnail: context.thumbnail,
    };
  }
}

/**
 * GroupSales-specific section data transformer.
 * Extends the base transformer to provide implementation for GeneralAvailability data.
 */
class GroupSalesSectionTransformer extends BaseSectionDataTransformer<GeneralAvailability> {
  transform(
    sectionAvailability: GeneralAvailability,
    context?: {
      node: MapViewerNode;
      thumbnail?: string;
      isSelectedForBA?: boolean;
      priceRangeWithFees?: string;
    }
  ): SectionPopoverData {
    if (!context) {
      throw new Error(
        'Context with MapViewerNode is required for section transformation'
      );
    }

    const firstPrice = sectionAvailability.priceRange?.[0]?.value;
    const secondPrice = sectionAvailability.priceRange?.[1]?.value;
    const min = Number.isInteger(firstPrice)
      ? firstPrice
      : Number(firstPrice.toFixed(2));

    const max = Number.isInteger(secondPrice)
      ? secondPrice
      : Number(secondPrice.toFixed(2));

    const priceRange = {
      min,
      max: max || min || 0,
    };

    const sectionType =
      context.node.type === 'general_admission'
        ? 'general_admission'
        : 'section';

    return {
      priceRange,
      id: sectionAvailability.sectionID,
      section:
        sectionAvailability.mmcID?.replace('S_', '') ||
        sectionAvailability.sectionID,
      availableQuantity: sectionAvailability.availableQuantity,
      adaQuantity: sectionAvailability.availableQuantityAda || 0,
      thumbnail: context.thumbnail,
      type: sectionType,
      isSelectedForBA: context.isSelectedForBA || false,
      priceRangeWithFees: context.priceRangeWithFees,
    };
  }
}

/**
 * Service to transform GroupSales data models to reusable popover component interfaces.
 * This service bridges the gap between GroupSales-specific data structures and the
 * generic popover components from the component library.
 */
@Injectable({
  providedIn: 'root',
})
export class PopoverDataTransformerService {
  private seatTransformer: GroupSalesSeatTransformer;
  private sectionTransformer: GroupSalesSectionTransformer;

  private utilitiesService = inject(UtilitiesService);
  constructor() {
    this.seatTransformer = new GroupSalesSeatTransformer(this.utilitiesService);
    this.sectionTransformer = new GroupSalesSectionTransformer();
  }

  /**
   * Transforms SeatAvailability data to SeatPopoverData interface.
   * @param seatAvailability - The seat availability data from GroupSales
   * @param node - The MapViewerNode for additional context
   * @param thumbnail - Optional thumbnail URL
   * @returns SeatPopoverData for the reusable seat popover component
   */
  transformSeatData(
    seatAvailability: SeatAvailability,
    node: MapViewerNode,
    thumbnail?: string
  ): SeatPopoverData {
    return this.seatTransformer.transform(seatAvailability, {
      node,
      thumbnail,
    });
  }

  /**
   * Transforms GeneralAvailability data to SectionPopoverData interface.
   * @param sectionAvailability - The section availability data from GroupSales
   * @param node - The MapViewerNode for additional context
   * @param thumbnail - Optional thumbnail URL
   * @param isSelectedForBA - Whether this section is selected for Best Available
   * @param priceRangeWithFees - Optional price range with fees calculated string
   * @returns SectionPopoverData for the reusable section popover component
   */
  transformSectionData(
    sectionAvailability: GeneralAvailability,
    node: MapViewerNode,
    thumbnail?: string,
    isSelectedForBA: boolean = false,
    priceRangeWithFees?: string
  ): SectionPopoverData {
    return this.sectionTransformer.transform(sectionAvailability, {
      node,
      thumbnail,
      isSelectedForBA,
      priceRangeWithFees,
    });
  }

  /**
   * Helper method to get the appropriate thumbnail section ID.
   * Handles special cases like S_BLCHADA mapping to S_Bleacher.
   * @param sectionId - The section ID
   * @returns The thumbnail section ID to use
   */
  getThumbnailSectionId(sectionId: string): string {
    // Handle special case for S_BLCHADA
    if (sectionId === 'S_BLCHADA') {
      return 'S_Bleacher';
    }
    return sectionId;
  }
}
